import type { RoomTemplate, TemplateField } from '@brush/api-client';
import { ArrowLeft, ChevronDown, ChevronUp, CircleAlert, Lock, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { purposeLabel } from '../../purposes';
import { csvEditorRules } from '../../templates/csvEditor';
import { acceptsMarker, markerChanged, markerForSave } from '../../templates/marker';
import { useUnsavedWork } from '../../unsaved';

const sameOrder = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * One template's content, for this room.
 *
 * Which editor appears is decided by the template's kind, never by the room: an HTML template
 * takes markup, a CSV one takes a choice and an order over the fields the platform defined.
 */
export function TemplateEditor({
  entry,
  saving,
  clearing,
  onSave,
  onClear,
  onBack,
}: {
  entry: RoomTemplate;
  saving: boolean;
  clearing: boolean;
  onSave: (body: {
    html: string | null;
    fieldKeys: string[] | null;
    unclaimedPlaceMarker: string | null;
  }) => void;
  onClear: () => void;
  onBack: () => void;
}) {
  const { template, content } = entry;
  const isCsv = template.kind === 'csv';
  const usedFor = purposeLabel(template.purpose);
  // PI69: the players template is matched by heading, in any order, so its editor sets no order.
  const rules = csvEditorRules(template);

  // A fresh fill-in starts with exactly the required fields, in the platform's order — the
  // smallest content the server will accept, so nothing has to be removed to save.
  const seededKeys = template.fields.filter((f) => f.required).map((f) => f.key);

  /*
   * What "unchanged" means on this screen, and the reason it is derived rather than a flag.
   *
   * Against the *saved* content where there is some, and against the seeded starting point where
   * there isn't — so opening an unfilled template and backing straight out asks nothing, while
   * adding a single column asks. It also resets itself: a successful save refreshes `content`,
   * the baseline becomes what was just stored, and the screen goes quiet without anything having
   * to remember that a save happened.
   */
  const baselineHtml = content?.html ?? '';
  const baselineKeys = content?.fieldKeys ?? seededKeys;

  const [html, setHtml] = useState(baselineHtml);
  const [keys, setKeys] = useState<string[]>(baselineKeys);

  // A results import's marker for "nobody has claimed this place" (ADR 0013). Saved with the
  // columns because it is, like them, a fact about the file.
  const takesMarker = acceptsMarker(template);
  const storedMarker = content?.unclaimedPlaceMarker ?? null;
  const [marker, setMarker] = useState(storedMarker ?? '');

  const dirty = isCsv
    ? !sameOrder(keys, baselineKeys) || (takesMarker && markerChanged(marker, storedMarker))
    : html !== baselineHtml;

  // Every way out of this screen routes through the same guard — the section nav and the room
  // switcher as much as the back button below.
  useUnsavedWork(dirty);

  const byKey = new Map(template.fields.map((f) => [f.key, f]));
  const chosen = keys.map((k) => byKey.get(k)).filter((f): f is TemplateField => f !== undefined);
  const available = template.fields.filter((f) => !keys.includes(f.key));

  const move = (index: number, by: number) =>
    setKeys((ks) => {
      const to = index + by;
      if (to < 0 || to >= ks.length) return ks;
      const next = [...ks];
      [next[index], next[to]] = [next[to]!, next[index]!];
      return next;
    });

  const valid = isCsv ? chosen.length > 0 : html.trim().length > 0;

  // No page margins of its own: this opens inside the Templates pane of Settings, which already
  // owns the padding the sticky footer below bleeds back over.
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="mb-6 flex items-center gap-2 text-sm text-ink-800/60 hover:text-ink-950"
      >
        <ArrowLeft className="size-4" /> Back to templates
      </button>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          {/* An `h2`: the page is Settings, and this is a screen within one of its panes. */}
          <h2 className="font-display text-xl font-semibold text-ink-950">{template.name}</h2>
          <p className="mt-1 text-sm text-ink-800/60">
            {isCsv ? rules.subtitle : 'Your markup for this template.'}
          </p>
          {/* What this one is for, when it is for something: a room choosing columns should know
              the choice is what the importer will read. */}
          {usedFor ? (
            <p className="mt-1 text-[11px] uppercase tracking-wide text-ink-800/50">{usedFor}</p>
          ) : null}
        </div>
        {content ? (
          <button
            type="button"
            onClick={onClear}
            disabled={clearing}
            className="shrink-0 rounded-lg border border-chip-500/40 px-4 py-2 text-sm text-chip-500 hover:bg-chip-500/5 disabled:opacity-50"
          >
            {clearing ? 'Clearing…' : 'Clear content'}
          </button>
        ) : null}
      </div>

      {isCsv ? (
        <div className="space-y-6">
          <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
            <header className="border-b border-ink-800/10 px-6 py-4">
              <h3 className="text-sm font-semibold text-ink-950">Columns in your file</h3>
              {rules.orderNote ? <p className="mt-1 text-xs text-ink-800/60">{rules.orderNote}</p> : null}
            </header>

            <ul className="divide-y divide-ink-800/5">
              {chosen.map((field, index) => (
                <li key={field.key} className="flex items-start gap-3 px-6 py-3">
                  {/* A position is a claim about the file's order, which the players template does not make. */}
                  {rules.reorder ? (
                    <span className="num mt-0.5 w-6 shrink-0 text-xs text-ink-800/40">
                      {index + 1}
                    </span>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink-950">{field.label}</span>
                      <span className="font-mono text-[11px] text-ink-800/50">{field.key}</span>
                      <span className="rounded bg-ink-800/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink-800/60">
                        {field.dataType}
                      </span>
                      {field.required ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-ink-800/40">
                          <Lock className="size-3" /> Required
                        </span>
                      ) : null}
                    </div>
                    {field.notes ? (
                      <p className="mt-0.5 text-xs text-ink-800/55">{field.notes}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center">
                    {rules.reorder ? (
                      <>
                        <button
                          type="button"
                          onClick={() => move(index, -1)}
                          disabled={index === 0}
                          aria-label={`Move ${field.label} earlier`}
                          className="p-1 text-ink-800/40 hover:text-ink-950 disabled:opacity-25"
                        >
                          <ChevronUp className="size-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(index, 1)}
                          disabled={index === chosen.length - 1}
                          aria-label={`Move ${field.label} later`}
                          className="p-1 text-ink-800/40 hover:text-ink-950 disabled:opacity-25"
                        >
                          <ChevronDown className="size-4" />
                        </button>
                      </>
                    ) : null}
                    {/* Required fields have no remove control at all, rather than one that
                        fails: the server would reject the save either way. */}
                    {field.required ? (
                      <span className="inline-block w-6" />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setKeys((ks) => ks.filter((k) => k !== field.key))}
                        aria-label={`Remove ${field.label}`}
                        className="p-1 text-ink-800/40 hover:text-chip-500"
                      >
                        <X className="size-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
              {chosen.length === 0 ? (
                <li className="px-6 py-8 text-center text-sm text-ink-800/50">
                  No columns chosen yet.
                </li>
              ) : null}
            </ul>
          </section>

          {takesMarker ? (
            <section className="rounded-xl border border-ink-800/10 bg-white p-5 shadow-sm">
              <label htmlFor="unclaimed-place-marker" className="text-sm font-semibold text-ink-950">
                Marker for an unclaimed place
              </label>
              <p className="mt-1 text-xs text-ink-800/60">
                If your results file writes a special value in the Card ID column for a place
                nobody has claimed yet, enter it here, and rows carrying it import as unclaimed
                places. Leave it blank if your file has no such value. It is matched without regard
                to case, and it cannot be a card any player at this room holds.
              </p>
              <input
                id="unclaimed-place-marker"
                value={marker}
                onChange={(e) => setMarker(e.target.value)}
                maxLength={64}
                spellCheck={false}
                placeholder="e.g. UNCLAIMED"
                className="mt-3 w-full max-w-xs rounded-lg border border-ink-800/15 px-3 py-2 font-mono text-sm text-ink-950 outline-none focus:border-felt-500"
              />
            </section>
          ) : null}

          {available.length > 0 ? (
            <section className="rounded-xl border border-dashed border-ink-800/15 p-5">
              <h3 className="text-sm font-semibold text-ink-950">Available columns</h3>
              <p className="mt-1 text-xs text-ink-800/60">{rules.availableNote}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {available.map((field) => (
                  <button
                    key={field.key}
                    type="button"
                    onClick={() => setKeys((ks) => [...ks, field.key])}
                    title={field.notes ?? undefined}
                    className="inline-flex items-center gap-1.5 rounded-full border border-ink-800/15 px-3 py-1.5 text-xs text-ink-800/80 transition hover:border-felt-500 hover:text-ink-950"
                  >
                    <Plus className="size-3" /> {field.label}
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      ) : (
        <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
          <header className="border-b border-ink-800/10 px-6 py-4">
            <h3 className="text-sm font-semibold text-ink-950">Markup</h3>
            <p className="mt-1 text-xs text-ink-800/60">
              Handlebars source. Saved as written — nothing here is rendered by this screen.
            </p>
          </header>
          <textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            spellCheck={false}
            rows={18}
            aria-label={`${template.name} markup`}
            placeholder="<div>{{room.name}}</div>"
            className="block w-full resize-y border-0 p-6 font-mono text-xs text-ink-950 outline-none"
          />
        </section>
      )}

      {/* Sticky, because the column list runs past a screen and the one thing an operator needs
          to know here — whether what they are looking at has been saved — must not be the thing
          that scrolls away. Full-bleed over the page's padding. */}
      <div className="sticky bottom-0 -mx-8 mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-ink-800/10 bg-ground/95 px-8 py-4 backdrop-blur">
        <span className="mr-auto">
          {dirty ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gold-500">
              <CircleAlert className="size-3.5" /> Unsaved changes
            </span>
          ) : content ? (
            <span className="text-xs text-ink-800/45">All changes saved</span>
          ) : (
            <span className="text-xs text-ink-800/45">Nothing saved for this template yet</span>
          )}
        </span>

        <button
          type="button"
          onClick={() =>
            onSave(
              isCsv
                ? { html: null, fieldKeys: keys, unclaimedPlaceMarker: takesMarker ? markerForSave(marker) : null }
                : { html, fieldKeys: null, unclaimedPlaceMarker: null },
            )
          }
          // Saving unchanged content is a no-op the server would still write, so it is only
          // offered when there is something to write — but a template with nothing stored can
          // always be saved, including with just the columns it was seeded with.
          disabled={!valid || saving || (content !== null && !dirty)}
          className="rounded-lg bg-felt-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-felt-900 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save content'}
        </button>
      </div>
    </div>
  );
}
